import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { 
  ArrowLeft, Save, Settings, BookOpen, MessageSquare, Eye, 
  Plus, Edit, Trash2, GripVertical, ChevronDown, ChevronUp
} from 'lucide-react';
import { courseService } from '../../../services/courseService';
import { firestoreService } from '../../../services/firestoreService';
import { 
  DndContext, closestCenter, KeyboardSensor, PointerSensor, 
  useSensor, useSensors 
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export const CourseEditor = ({ bookId, courseId, user, onBack, embedded = false }) => {
  const navigate = useNavigate();
  const isEditing = courseId !== 'new';

  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedLessons, setExpandedLessons] = useState(new Set());
  
  const [currentStep, setCurrentStep] = useState(1);
  const [learningObjectives, setLearningObjectives] = useState(['']);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      title: '',
      description: '',
      shortDescription: '',
      category: '',
      level: 'Beginner',
      isPremium: false,
      price: 0,
      tags: '',
      learningObjectives: '',
      prerequisites: '',
      targetAudience: '',
      duration: 30,
      durationUnit: 'minutes',
      offersVirtualSessions: false,
      isPublished: false
    }
  });

  const isPremium = watch('isPremium');

  const categories = [
    'Business', 'Digital Skills', 'Career Development', 'Entrepreneurship',
    'Personal Development', 'Technical Skills', 'Communication', 'Leadership',
    'Custom Category'
  ];

  const levels = ['Beginner', 'Intermediate', 'Advanced'];

  const durationUnits = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' }
  ];

  const tabs = [
    { id: 'details', label: 'Course Details', icon: Settings },
    { id: 'lessons', label: 'Lessons', icon: BookOpen },
    { id: 'virtual-sessions', label: 'Virtual Sessions', icon: MessageSquare },
    { id: 'preview', label: 'Preview', icon: Eye }
  ];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (isEditing) {
      loadCourse();
    }
  }, [courseId]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      
      const courseData = await courseService.getCourse(bookId, courseId);
      if (!courseData || courseData.creatorId !== user.uid) {
        navigate('/creator');
        return;
      }

      setCourse(courseData);
      
      Object.keys(courseData).forEach(key => {
        if (key === 'tags' && Array.isArray(courseData[key])) {
          setValue(key, courseData[key].join(', '));
        } else if (key === 'learningObjectives' && Array.isArray(courseData[key])) {
          setLearningObjectives(courseData[key].length > 0 ? courseData[key] : ['']);
        } else {
          setValue(key, courseData[key]);
        }
      });

      if (courseData.thumbnail) {
        setThumbnailUrl(courseData.thumbnail.url);
      }

      const lessonsData = await courseService.getLessons(bookId, courseId);
      setLessons(lessonsData);
      
    } catch (error) {
      console.error('Error loading course:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailUpload = async (file) => {
    try {
      setThumbnailFile(file);
      const url = await firestoreService.uploadFile(
        file,
        `pb-${bookId}/courses/${Date.now()}_${file.name}`,
        (progress) => setThumbnailUploadProgress(progress)
      );
      setThumbnailUrl(url);
      setValue('thumbnail', url);
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      alert('Failed to upload thumbnail');
    }
  };

  const handleThumbnailRemove = () => {
    setThumbnailFile(null);
    setThumbnailUrl('');
    setThumbnailUploadProgress(0);
    setValue('thumbnail', '');
  };

  const handleBack = () => {
    if (embedded && onBack) {
      onBack();
    } else {
      navigate('creator');
    }
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      
      const tagsArray = data.tags ? 
        data.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [];
      
      const objectivesArray = learningObjectives.filter(obj => obj.trim());

      let durationInMinutes = parseInt(data.duration);
      switch (data.durationUnit) {
        case 'hours':
          durationInMinutes *= 60;
          break;
        case 'days':
          durationInMinutes *= 60 * 24;
          break;
        case 'weeks':
          durationInMinutes *= 60 * 24 * 7;
          break;
      }

      const courseData = {
        ...data,
        creatorId: user.uid,
        creatorName: user.displayName || user.email,
        tags: tagsArray,
        learningObjectives: objectivesArray,
        estimatedDuration: durationInMinutes,
        displayDuration: {
          value: parseInt(data.duration),
          unit: data.durationUnit
        },
        price: isPremium ? parseFloat(data.price) : 0,
        thumbnail: thumbnailUrl || null,
        duration: lessons.reduce((total, lesson) => total + (lesson.duration || 0), 0),
        lessonCount: lessons.length
      };

      if (isEditing) {
        await courseService.updateCourse(bookId, courseId, courseData);
        setCourse({ ...course, ...courseData });
        alert('Course updated successfully!');
      } else {
        const newCourseId = await courseService.createCourse(bookId, courseData);
        alert('Course created successfully!');
        navigate(`/creator/course/${newCourseId}/edit`);
      }
    } catch (error) {
      console.error('Error saving course:', error);
      alert('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleAddLesson = () => {
    if (isEditing) {
      navigate(`/creator/course/${courseId}/lesson/new`);
    } else {
      alert('Please save the course first before adding lessons');
    }
  };

  const handleEditLesson = (lessonId) => {
    navigate(`/creator/course/${courseId}/lesson/${lessonId}/edit`);
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;

    try {
      await courseService.deleteLesson(bookId, courseId, lessonId);
      setLessons(lessons.filter(l => l.id !== lessonId));
      alert('Lesson deleted successfully');
    } catch (error) {
      console.error('Error deleting lesson:', error);
      alert('Failed to delete lesson');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = lessons.findIndex(l => l.id === active.id);
      const newIndex = lessons.findIndex(l => l.id === over.id);
      
      const reordered = arrayMove(lessons, oldIndex, newIndex);
      setLessons(reordered);

      try {
        await courseService.reorderLessons(bookId, courseId, reordered.map(l => l.id));
      } catch (error) {
        console.error('Error reordering lessons:', error);
        setLessons(lessons);
      }
    }
  };

  const toggleLessonExpanded = (lessonId) => {
    const newExpanded = new Set(expandedLessons);
    if (newExpanded.has(lessonId)) {
      newExpanded.delete(lessonId);
    } else {
      newExpanded.add(lessonId);
    }
    setExpandedLessons(newExpanded);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const renderDetailsTab = () => (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Course Title *
            </label>
            <input
              type="text"
              {...register('title', { required: 'Title is required' })}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.title && (
              <p className="text-red-600 text-sm mt-1">{errors.title.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              {...register('category', { required: 'Category is required' })}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select Category</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            {errors.category && (
              <p className="text-red-600 text-sm mt-1">{errors.category.message}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Short Description *
          </label>
          <input
            type="text"
            {...register('shortDescription', { required: 'Short description is required' })}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors.shortDescription && (
            <p className="text-red-600 text-sm mt-1">{errors.shortDescription.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Description *
          </label>
          <textarea
            {...register('description', { required: 'Description is required' })}
            rows={6}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors.description && (
            <p className="text-red-600 text-sm mt-1">{errors.description.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Level *
            </label>
            <select
              {...register('level')}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {levels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration
            </label>
            <input
              type="number"
              {...register('duration')}
              min="1"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unit
            </label>
            <select
              {...register('durationUnit')}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {durationUnits.map(unit => (
                <option key={unit.value} value={unit.value}>{unit.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Prerequisites
          </label>
          <textarea
            {...register('prerequisites')}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Audience
          </label>
          <textarea
            {...register('targetAudience')}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              {...register('isPremium')}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Premium Course</span>
          </label>

          {isPremium && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price (USD)
              </label>
              <input
                type="number"
                {...register('price')}
                min="0"
                step="0.01"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}
        </div>

        <div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register('offersVirtualSessions')}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Virtual Sessions</span>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tags
          </label>
          <input
            type="text"
            {...register('tags')}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Separate tags with commas"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Course Thumbnail
          </label>
          {thumbnailUrl ? (
            <div className="relative">
              <img src={thumbnailUrl} alt="Thumbnail" className="w-full h-48 object-cover rounded-lg" />
              <button
                type="button"
                onClick={handleThumbnailRemove}
                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
              >
                ×
              </button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <label className="cursor-pointer">
                <span className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
                  Upload Image
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files[0] && handleThumbnailUpload(e.target.files[0])}
                  className="hidden"
                />
              </label>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            <Save className="w-5 h-5" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </form>
    </div>
  );

  const SortableLesson = ({ lesson }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: lesson.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const isExpanded = expandedLessons.has(lesson.id);

    return (
      <div
        ref={setNodeRef}
        style={style}
        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
            >
              <GripVertical className="w-5 h-5" />
            </button>

            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">{lesson.title}</h3>
              <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                <span>{lesson.duration || 0} min</span>
                <span>{lesson.contentBlocks?.length || 0} blocks</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => toggleLessonExpanded(lesson.id)}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            <button
              onClick={() => handleEditLesson(lesson.id)}
              className="p-2 text-gray-400 hover:text-blue-600"
            >
              <Edit className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleDeleteLesson(lesson.id)}
              className="p-2 text-gray-400 hover:text-red-600"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        {isExpanded && lesson.description && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">{lesson.description}</p>
          </div>
        )}
      </div>
    );
  };

  const renderLessonsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Course Lessons</h3>
          <p className="text-gray-600">Manage and organize your course content</p>
        </div>
        <button
          onClick={handleAddLesson}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Add Lesson</span>
        </button>
      </div>

      {lessons.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No lessons yet</h3>
          <p className="text-gray-600 mb-6">Start building your course by adding lessons</p>
          <button
            onClick={handleAddLesson}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Create First Lesson
          </button>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={lessons.map(l => l.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {lessons.map((lesson) => (
                <SortableLesson key={lesson.id} lesson={lesson} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );

  const renderVirtualSessionsTab = () => (
    <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
      <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Virtual Sessions</h3>
      <p className="text-gray-600">Configure virtual 1-on-1 sessions for this course</p>
    </div>
  );

  const renderPreviewTab = () => (
    <div className="bg-white rounded-xl border border-gray-200 p-8">
      <h3 className="text-xl font-bold text-gray-900 mb-6">Course Preview</h3>
      <div className="space-y-4">
        <div>
          <h4 className="font-semibold text-gray-900">{course?.title}</h4>
          <p className="text-gray-600">{course?.description}</p>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span>{lessons.length} lessons</span>
          <span>{course?.level}</span>
          <span>{course?.isPremium ? `$${course.price}` : 'Free'}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button onClick={handleBack} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Course' : 'Create Course'}
            </h1>
            <p className="text-gray-600">{course?.title || 'New Course'}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="flex gap-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && renderDetailsTab()}
      {activeTab === 'lessons' && renderLessonsTab()}
      {activeTab === 'virtual-sessions' && renderVirtualSessionsTab()}
      {activeTab === 'preview' && renderPreviewTab()}
    </div>
  );
};